import React, { useEffect, useRef, useState, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase.ts';
import { useNavigate } from 'react-router-dom';
import './WeeklyMode.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.quizvs.com';
const QUESTION_TIME = 15; // seconds per question

interface WeeklyQuestion {
  id: number;
  question: string;
  options: string[];
  correct_answer_index: number; // 1-based
  imageUrl?: string;
  difficulty: number;
  category: string;
  category_name?: string;
  category_emoji?: string;
}

const WeeklyGame: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Game state
  const [question, setQuestion] = useState<WeeklyQuestion | null>(null);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const [wave, setWave] = useState(1);
  const [livesRemaining, setLivesRemaining] = useState(3);
  const [score, setScore] = useState(0);

  // Life lost animation
  const [lifeLostIndex, setLifeLostIndex] = useState<number | null>(null);

  // Timer
  const [timer, setTimer] = useState(QUESTION_TIME);
  const [running, setRunning] = useState(false);
  const timerRef = useRef<any>(null);

  // Answer feedback
  const [showFeedback, setShowFeedback] = useState(false);
  const [lastCorrect, setLastCorrect] = useState<boolean | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [correctAnswerText, setCorrectAnswerText] = useState<string>('');

  // Game over / completed
  const [gameOver, setGameOver] = useState(false);
  const [gameOverReason, setGameOverReason] = useState<'wrong' | 'timeout' | 'cheat' | 'no_lives' | null>(null);
  const [completed, setCompleted] = useState(false);
  const [completedMessage, setCompletedMessage] = useState('');

  // Wave-up toast
  const [showWaveUp, setShowWaveUp] = useState(false);
  const [newWaveNum, setNewWaveNum] = useState(1);

  // Error
  const [error, setError] = useState<string | null>(null);

  // Prevent double-submit
  const submittingRef = useRef(false);

  // Auth gate
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
      if (!u) navigate('/login');
    });
    return () => unsub();
  }, [navigate]);

  // Anti-cheat: tab hidden → game over
  useEffect(() => {
    function onVisibility() {
      if (document.hidden && running && !gameOver && !completed) {
        handleGameOver('cheat');
      }
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [running, gameOver, completed]);

  // Fetch next question
  const fetchNext = useCallback(async () => {
    if (!user) return;
    setError(null);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_URL}/api/weekly/next`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ language: 'fr' })
      });
      const data = await res.json();

      // No lives
      if (data.error === 'NO_LIVES') {
        setGameOver(true);
        setGameOverReason('no_lives');
        return;
      }

      // All questions completed for today
      if (data.completed) {
        setCompleted(true);
        setCompletedMessage(data.message || '🎉 Thème terminé !');
        return;
      }

      // Skip (question unavailable, try again)
      if (data.skip) {
        // Retry immediately
        setTimeout(() => fetchNext(), 200);
        return;
      }

      if (!data.question) {
        setError('Question non trouvée');
        return;
      }

      setQuestion(data.question);
      setProgress(data.progress);
      setWave(data.wave || 1);
      if (typeof data.livesRemaining === 'number') {
        setLivesRemaining(data.livesRemaining);
      }
      setTimer(QUESTION_TIME);
      setRunning(true);
    } catch (e: any) {
      console.error('[WeeklyGame] fetchNext error:', e);
      setError(e?.message || 'Erreur réseau');
    }
  }, [user]);

  // Start game on mount
  useEffect(() => {
    if (!loading && user) {
      fetchNext();
    }
  }, [loading, user, fetchNext]);

  // Timer countdown
  useEffect(() => {
    if (!running) return;
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          handleGameOver('timeout');
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [running]);

  // Submit answer to server
  const submitAnswer = useCallback(async (questionId: number, selectedText: string, timeTaken: number) => {
    if (!user || submittingRef.current) return null;
    submittingRef.current = true;
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_URL}/api/weekly/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ questionId, selectedAnswer: selectedText, timeTaken })
      });
      return await res.json();
    } catch (e) {
      console.error('[WeeklyGame] submit error:', e);
      return null;
    } finally {
      submittingRef.current = false;
    }
  }, [user]);

  // Handle game over
  function handleGameOver(reason: 'wrong' | 'timeout' | 'cheat' | 'no_lives') {
    setRunning(false);
    clearInterval(timerRef.current);
    setGameOver(true);
    setGameOverReason(reason);

    // On timeout, submit the wrong answer and get correct answer
    if (reason === 'timeout' && question) {
      const correctIdx = question.correct_answer_index - 1;
      setCorrectAnswerText(question.options[correctIdx] || '');
      submitAnswer(question.id, '__TIMEOUT__', QUESTION_TIME);
    }

    if (reason === 'cheat' && question) {
      submitAnswer(question.id, '__CHEAT__', 0);
    }
  }

  // Handle answer click
  async function handleAnswer(choiceIndex: number) {
    if (!question || showFeedback || gameOver || completed) return;

    setSelectedAnswer(choiceIndex);
    setRunning(false);
    clearInterval(timerRef.current);

    const selectedText = question.options[choiceIndex];
    const isCorrect = (choiceIndex + 1) === Number(question.correct_answer_index);
    setLastCorrect(isCorrect);
    setShowFeedback(true);

    // Submit to server
    const timeTaken = QUESTION_TIME - timer;
    const result = await submitAnswer(question.id, selectedText, timeTaken);

    if (result) {
      const prevLives = livesRemaining;
      const newLives = typeof result.livesRemaining === 'number' ? result.livesRemaining : prevLives;
      setLivesRemaining(newLives);
      setScore(result.newStats?.rawScore ?? score);

      // Trigger life-lost animation (animate the heart that just disappeared)
      if (!isCorrect && newLives < prevLives) {
        setLifeLostIndex(newLives); // index of the heart going from active→empty
        setTimeout(() => setLifeLostIndex(null), 1500);
      }

      if (!isCorrect) {
        setCorrectAnswerText(result.correctAnswer || '');
      }

      // Wave up toast
      if (result.waveUp) {
        setNewWaveNum(result.newStats?.currentWave || wave + 1);
        setShowWaveUp(true);
        setTimeout(() => setShowWaveUp(false), 3000);
      }
    }

    // Delay before next action
    setTimeout(() => {
      setShowFeedback(false);
      setSelectedAnswer(null);
      setLastCorrect(null);

      if (!isCorrect) {
        const noLives = (result && typeof result.livesRemaining === 'number') ? result.livesRemaining <= 0 : false;
        if (result?.gameOver || noLives) {
          // No lives left → game over – force 0 lives in state
          setLivesRemaining(0);
          setGameOver(true);
          setGameOverReason('no_lives');
        } else {
          // Still have lives → continue to next question
          setQuestion(null);
          fetchNext();
        }
      } else {
        // Correct: load next question
        setQuestion(null);
        fetchNext();
      }
    }, 2000);
  }

  if (loading) {
    return <div className="weekly-container" style={{ textAlign: 'center', paddingTop: 80 }}>Chargement...</div>;
  }

  // Completed → redirect
  if (completed) {
    return (
      <div className="weekly-container">
        <div className="weekly-completed">
          <div className="weekly-completed-emoji">🎉</div>
          <h2 className="weekly-completed-title">Thème terminé !</h2>
          <p className="weekly-completed-desc">{completedMessage}</p>
          <div className="weekly-completed-stats">
            <div className="weekly-completed-stat">
              <div className="weekly-completed-stat-value">{score}</div>
              <div className="weekly-completed-stat-label">points cette semaine</div>
            </div>
          </div>
          <button className="weekly-completed-btn" onClick={() => navigate('/weekly')}>
            ← Retour au Hub
          </button>
        </div>
      </div>
    );
  }

  // Game Over overlay
  if (gameOver) {
    // Force 0 lives display when game over reason is no_lives
    const displayLives = gameOverReason === 'no_lives' ? 0 : livesRemaining;
    return (
      <div className="weekly-gameover-overlay">
        <div className="weekly-gameover-card">
          <div className="weekly-gameover-emoji">
            {gameOverReason === 'wrong' && '💥'}
            {gameOverReason === 'timeout' && '⏰'}
            {gameOverReason === 'cheat' && '🚫'}
            {gameOverReason === 'no_lives' && '💀'}
          </div>
          <h3 className="weekly-gameover-title">
            {gameOverReason === 'wrong' && 'Mauvaise réponse !'}
            {gameOverReason === 'timeout' && 'Temps écoulé !'}
            {gameOverReason === 'cheat' && 'Triche détectée !'}
            {gameOverReason === 'no_lives' && 'Plus de vies !'}
          </h3>

          {correctAnswerText && (
            <div className="weekly-gameover-correct">
              <span>La bonne réponse était :</span>
              <strong>✅ {correctAnswerText}</strong>
            </div>
          )}

          <div className="weekly-gameover-lives">
            {Array.from({ length: 3 }).map((_, i) => (
              <span key={i} style={{ opacity: i < displayLives ? 1 : 0.2, filter: i < displayLives ? 'none' : 'grayscale(1)' }}>❤️</span>
            ))}
          </div>
          {displayLives < 3 && displayLives > 0 && (
            <div className="weekly-gameover-regen">+1 vie toutes les 8h</div>
          )}

          <div className="weekly-gameover-stats">
            <div className="weekly-gameover-stat">
              <div className="weekly-gameover-stat-value">{score}</div>
              <div className="weekly-gameover-stat-label">score total</div>
            </div>
            <div className="weekly-gameover-stat">
              <div className="weekly-gameover-stat-value">🌊 {wave}</div>
              <div className="weekly-gameover-stat-label">vague</div>
            </div>
          </div>

          {displayLives > 0 && gameOverReason !== 'no_lives' ? (
            <button className="weekly-gameover-btn" onClick={() => {
              setGameOver(false);
              setGameOverReason(null);
              setCorrectAnswerText('');
              setQuestion(null);
              fetchNext();
            }}>
              ▶️ Continuer ({displayLives} ❤️)
            </button>
          ) : (
            <div className="weekly-no-lives">
              <div className="weekly-no-lives-emoji">💀</div>
              <div className="weekly-no-lives-title">Plus de vies disponibles</div>
              <div className="weekly-no-lives-desc">+1 vie toutes les 8h</div>
            </div>
          )}
          <button className="weekly-gameover-btn" onClick={() => navigate('/weekly')} style={{ marginTop: 12, background: 'rgba(255,255,255,0.08)' }}>
            ← Retour au Hub
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="weekly-container">
      {/* Game header */}
      <div className="weekly-game-header">
        <button className="weekly-back-btn" onClick={() => navigate('/weekly')}>← Quitter</button>
        <div className="weekly-game-info">
          <span className="weekly-wave-badge">🌊 Vague {wave}</span>
          {progress && (
            <span className="weekly-game-progress">
              {progress.current}/{progress.total}
            </span>
          )}
        </div>
        <div className="weekly-game-lives">
          {Array.from({ length: 3 }).map((_, i) => {
            const isLost = i === lifeLostIndex;
            const isActive = !isLost && i < livesRemaining;
            const isEmpty = !isLost && i >= livesRemaining;
            let cls = 'weekly-life-heart';
            if (isLost) cls += ' life-lost';
            else if (isActive) cls += ' active';
            else if (isEmpty) cls += ' empty';
            // Use a unique key when life-lost triggers to force re-mount for animation
            const uniqueKey = isLost ? `lost-${i}-${Date.now()}` : `heart-${i}`;
            return <span key={uniqueKey} className={cls}>❤️</span>;
          })}
        </div>
      </div>

      {/* Timer */}
      <div className="weekly-timer-container">
        <div className={`weekly-timer ${timer <= 5 ? 'danger' : ''}`}>
          <div className="weekly-timer-ring"></div>
          <span>{timer}</span>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: 20, textAlign: 'center', color: '#ef4444' }}>
          ❌ {error}
          <br />
          <button className="weekly-back-btn" onClick={() => fetchNext()} style={{ marginTop: 10 }}>
            Réessayer
          </button>
        </div>
      )}

      {/* Question card */}
      {question ? (
        <div className="weekly-question-card">
          <div className="weekly-question-category">
            {question.category_emoji || '📝'} {question.category_name || question.category}
            <span style={{ marginLeft: 8, color: 'rgba(255,215,0,0.6)' }}>
              {Array.from({ length: question.difficulty }, (_, i) => '★').join('')}
            </span>
          </div>

          <div className="weekly-question-text">{question.question}</div>

          {question.imageUrl && (
            <div className="weekly-question-image">
              <img
                src={question.imageUrl}
                alt="Question"
                style={{
                  maxWidth: '300px',
                  maxHeight: '200px',
                  borderRadius: '8px',
                  objectFit: 'contain',
                  border: '2px solid var(--accent-color, #6366f1)',
                  margin: '16px auto',
                  display: 'block',
                }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            </div>
          )}

          <div className="weekly-options">
            {question.options.map((opt, i) => {
              let btnClass = 'weekly-option-btn';
              if (showFeedback) {
                const correctIdx = question.correct_answer_index - 1;
                if (i === correctIdx) btnClass += ' correct';
                else if (i === selectedAnswer) btnClass += ' wrong';
              }
              if (showFeedback || !running) btnClass += ' disabled';

              return (
                <button
                  key={`${question.id}-${i}-${opt.substring(0, 10)}`}
                  onClick={() => handleAnswer(i)}
                  className={btnClass}
                  disabled={!running || showFeedback}
                >
                  <strong>{String.fromCharCode(65 + i)}.</strong> {opt}
                </button>
              );
            })}
          </div>
        </div>
      ) : !error ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.5)' }}>
          ⚡ Chargement de la question...
        </div>
      ) : null}

      {/* Wave-up toast */}
      {showWaveUp && (
        <div className="weekly-wave-up-toast">
          🌊 Vague {newWaveNum} ! Difficulté augmentée !
        </div>
      )}
    </div>
  );
};

export default WeeklyGame;
